import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { authAPI } from '../lib/api';
import { isTenantUser, normalizeRole } from '../lib/authRoles';
import { isAnonymousAuthFlowRoute, clearStoredSession } from '../lib/apiAuth';
import { formatApiErrorDetail } from '../lib/emailValidation';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const ensurePreferences = (userData) => {
    const merged = {
      language: 'en',
      unit_system: 'imperial',
      currency: 'USD',
      ...(userData?.preferences || {}),
    };
    const role = userData?.role ? normalizeRole(userData.role) : userData?.role;
    return { ...userData, preferences: merged, role };
  };

  const syncPreferenceStorage = (preferences) => {
    if (!preferences) return;
    if (preferences.language) localStorage.setItem('language', preferences.language);
    if (preferences.unit_system) localStorage.setItem('unitSystem', preferences.unit_system);
    if (preferences.currency) localStorage.setItem('currency', preferences.currency);
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (isAnonymousAuthFlowRoute()) {
        clearStoredSession();
        if (!cancelled) setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token) {
        try {
          const response = await authAPI.getMe();
          if (!cancelled) {
            const fresh = ensurePreferences(response.data);
            localStorage.setItem('user', JSON.stringify(fresh));
            syncPreferenceStorage(fresh.preferences);
            setUser(fresh);
          }
        } catch {
          clearStoredSession();
          if (!cancelled) setUser(null);
        }
      } else {
        if (savedUser) {
          localStorage.removeItem('user');
        }
        if (!cancelled) setUser(null);
      }

      if (!cancelled) setLoading(false);
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const applySession = (token, userData) => {
    const normalized = ensurePreferences(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(normalized));
    syncPreferenceStorage(normalized.preferences);
    setUser(normalized);
    return normalized;
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { token, user: userData } = response.data;
      
      const normalizedUser = applySession(token, userData);
      return { success: true, user: normalizedUser };
    } catch (err) {
      const message = formatApiErrorDetail(err.response?.data?.detail, 'Login failed');
      setError(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      const response = await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      const updated = ensurePreferences(response.data.user);
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Password update failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authAPI.register(userData);
      const { token, user: newUser } = response.data;

      const normalizedUser = applySession(token, newUser);
      return { success: true, user: normalizedUser };
    } catch (err) {
      const message = formatApiErrorDetail(err.response?.data?.detail, 'Registration failed');
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('language');
    localStorage.removeItem('currency');
    localStorage.removeItem('unitSystem');
    setUser(null);
  };

  const updateUserPreferences = (preferences) => {
    if (user) {
      const updatedUser = { ...user, preferences: { ...user.preferences, ...preferences } };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      syncPreferenceStorage(updatedUser.preferences);
      setUser(updatedUser);
    }
  };

  const isTenant = useMemo(() => isTenantUser(user), [user]);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUserPreferences,
    changePassword,
    applySession,
    mustChangePassword: Boolean(user?.must_change_password),
    isAuthenticated: !!user,
    isTenant,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
